import { useQuery } from '@tanstack/react-query'
import { useCallback } from 'react'
import { useAccessToken } from '../../useAccessToken'
import useProjectId from '../../../customHooks/useProjectId'
import { useProjektprognosInteractionContext } from '../../../context/ProjektprognosInteractionContext'
import { formatData } from '../../../../../utils/formatData'
import { makeResultFetch } from '../../genericFetch'
import { apiRequestParams } from '../../fetchOptions'
import { GridRow } from '../../../../../interfaces/grids/GridRow'
import { useHandleRetry } from '../../useHandleRetry'

const useFetch = () => {
  const getAccessToken = useAccessToken()
  const projectId = useProjectId()

  const queryFunction = useCallback(async () => {
    return getAccessToken().then((accessToken) => {
      if (!accessToken) throw new Error('Access token is undefined')

      const fetchedData = makeResultFetch<GridRow[]>(
        `/Sheet/${projectId}/Cashflow`,
        apiRequestParams(accessToken, 'GET')
      ).then((response) => {
        const { data, message, success } = response

        if (!success) throw Error(message)

        if (data) {
          const formattedData = formatData(data)
          return formattedData
        }
      })

      return fetchedData
    })
  }, [getAccessToken, projectId])

  return { projectId, queryFunction }
}

export const useProjektprognosCashflow = (options?: { enabled?: boolean }) => {
  const { projectId, queryFunction } = useFetch()
  const { loadProjektprognos } = useProjektprognosInteractionContext()
  const handleRetry = useHandleRetry()

  const { isLoading, error, data } = useQuery({
    queryKey: [projectId, 'cashflow'],
    queryFn: queryFunction,
    enabled:
      !!projectId &&
      !!queryFunction &&
      // When an explicit enabled value is provided by the caller, use it directly
      // (bypasses loadProjektprognos so the query can re-enable after the initial load).
      // When no option is passed, fall back to loadProjektprognos as the initial-load gate.
      (options?.enabled !== undefined ? options.enabled : loadProjektprognos),
    retry: handleRetry,
  })

  return { isLoading, error, data }
}
